const focusKeys = new Set(["id", "type", "title", "lines", "markdown", "focusLines", "questions", "images", "conceptGroupId", "conceptGroupTitle", "chapter"]);
const sourceText = (value) => Array.isArray(value) ? value.join("\n") : String(value ?? "");
const validatedFocusSource = (section) => {
  const sources = [section.markdown, section.lines, section.focusLines];
  if (sources.some((value) => value != null && typeof value !== "string" &&
    (!Array.isArray(value) || value.some((line) => typeof line !== "string")))) return null;
  const nonempty = sources.map(sourceText).filter(Boolean);
  return new Set(nonempty).size > 1 ? null : nonempty[0] || "";
};
const metadataKeys = ["conceptGroupId", "conceptGroupTitle"];
const withoutGroupMetadata = (section) => Object.fromEntries(Object.entries(section).filter(([key]) => !metadataKeys.includes(key)));
const validId = (value) => typeof value === "string" && Boolean(value.trim());
const hasQuestions = (section) => section.questions !== undefined && (!Array.isArray(section.questions) || section.questions.length > 0);

// Old clients still see each source block. New readers assemble only explicitly marked, lossless runs.
export const groupHandoutConceptSections = (sections = []) => {
  if (!Array.isArray(sections)) return [];
  const result = [];
  for (let index = 0; index < sections.length;) {
    const first = sections[index];
    if (!first || !validId(first.conceptGroupId)) { result.push(first); index += 1; continue; }
    let end = index + 1;
    while (end < sections.length && sections[end]?.conceptGroupId === first.conceptGroupId) end += 1;
    const run = sections.slice(index, end);
    const focuses = run.filter((section) => section?.type === "focus");
    const titles = run.map((section) => section?.conceptGroupTitle).filter(validId);
    const title = titles[0];
    const valid = focuses.length > 0 && focuses.slice(1).every((section) => !section.chapter) && validId(title) && new Set(titles).size === 1 && run.every((section) =>
      section && ["focus", "image"].includes(section.type) && !hasQuestions(section) &&
      (section.type !== "image" || ((section.asset && typeof section.asset === "object" && !Array.isArray(section.asset)) || typeof section.url === "string")) &&
      (section.type !== "focus" || (validatedFocusSource(section) !== null && !/^### /m.test(validatedFocusSource(section)) &&
        (section.images === undefined || (Array.isArray(section.images) && section.images.every((image) => image && typeof image === "object" && !Array.isArray(image) &&
          ((image.asset && typeof image.asset === "object" && !Array.isArray(image.asset)) || typeof image.url === "string")))) &&
        Object.keys(section).every((key) => focusKeys.has(key)))));
    if (!valid) { result.push(...run); index = end; continue; }
    const parts = focuses.map((section, ordinal) => {
      const subtitle = String(section.title || "重點整理").split("・").pop().trim();
      return `### ${ordinal + 1}. ${subtitle}\n\n${validatedFocusSource(section)}`;
    });
    const images = [];
    let ordinal = -1;
    const addImage = (image, anchorSection) => {
      const sourceWidth = image.widthCm ?? image.width_cm;
      const parsedWidth = typeof sourceWidth === "boolean" ? NaN : Number(sourceWidth);
      const widthCm = Number.isFinite(parsedWidth) && parsedWidth ? parsedWidth : 4;
      const alignment = ["left", "center", "right"].includes(image.alignment) ? image.alignment : widthCm > 6 ? "center" : "right";
      const layout = alignment === "center" ? "block" : ["float", "block"].includes(image.layout) ? image.layout : widthCm > 6 ? "block" : "float";
      const { type, questions, anchor_section, ...sourceImage } = withoutGroupMetadata(image);
      images.push({ ...sourceImage, widthCm, alignment, layout, anchorSection });
      const caption = sourceText(image.text);
      if (caption.trim() && !parts[anchorSection].includes(caption)) parts[anchorSection] += `\n\n${caption}`;
    };
    run.forEach((section) => {
      if (section.type === "focus") {
        ordinal += 1;
        (section.images || []).forEach((image) => addImage(image, ordinal));
      } else {
        const { type, questions, ...image } = section;
        const anchorSection = Math.max(0, ordinal);
        addImage(image, anchorSection);

      }
    });
    const text = parts.join("\n\n");
    const { focusLines, ...sourceFocus } = withoutGroupMetadata(focuses[0]);
    result.push({ ...sourceFocus, title, lines: text, markdown: text, images });
    index = end;
  }
  return result;
};
